import moment from 'moment';

import { brandCode } from 'constants/brandCodes';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { bookingStatus } from 'types/booking';
import { types as passengerTypes } from 'types/passengers';
import { isCloneExternalTicketsEnabled } from 'utils/featureFlags';
import { isUserCallCenterAgent, isUserInCSCOffice, isUserInEBSCOffice } from 'utils/user';

export const FARE_CONDITIONS = {
  NON_CANCELLABLE: 'NON_CANCELLABLE',
  PARTIALLY_OR_NON_REFUNDABLE: 'PARTIALLY_OR_NON_REFUNDABLE',
  FULLY_REFUNDABLE: 'FULLY_REFUNDABLE'
};

const SUPPORTED_BRAND_CODES = new Set([brandCode.DB, brandCode.Lyria, brandCode.NS, brandCode.SNCB, brandCode.SNCF, brandCode.INOUI]);

const SUPPORTED_SERVICE_CODES = new Set([serviceTypeCodes.DB, serviceTypeCodes.NS, serviceTypeCodes.SNCB, serviceTypeCodes.SNCF]);

const isProductCancelled = (product = {}) => Boolean(product.cancelled || product.isCancelled);

const isProductConfirmed = (product = {}) =>
  !isProductCancelled(product) && !product.provisional && product.isActive !== false && !product.isSuspended && !product.isPlaceholder;

export const isSupportedExternalLeg = ({ serviceType = {} } = {}) => {
  if (serviceType.brandCode) {
    return SUPPORTED_BRAND_CODES.has(serviceType.brandCode);
  }

  return SUPPORTED_SERVICE_CODES.has(serviceType.code);
};

const normaliseDirection = (direction) => direction?.toLowerCase();

export const canUserCloneExternalTickets = (user) =>
  isCloneExternalTicketsEnabled() && isUserCallCenterAgent(user) && (isUserInEBSCOffice(user) || isUserInCSCOffice(user));

export const getSelectedExternalLeg = ({ booking = {}, selectedItemRefs = [] }) => {
  const selectedRefs = new Set(selectedItemRefs.map(String));
  const journeyLegs = ['outbound', 'inbound'].flatMap((direction) =>
    (booking[direction] || []).flatMap((leg) => [
      { ...leg, direction: normaliseDirection(leg.direction || direction) },
      ...(leg.addOnLeg ? [{ ...leg.addOnLeg, direction: normaliseDirection(leg.addOnLeg.direction || leg.direction || direction) }] : [])
    ])
  );
  const uniqueLegs = journeyLegs.filter((leg, index) => {
    const legKey = leg.id || leg.uuid;

    return !legKey || journeyLegs.findIndex(({ id, uuid }) => (id || uuid) === legKey) === index;
  });
  const selectedLegs = uniqueLegs
    .filter((leg) => !leg.cancelled)
    .map((leg) => ({
      leg,
      products: (leg.products || []).filter(({ itemRef }) => selectedRefs.has(String(itemRef)))
    }))
    .filter(({ products }) => products.length > 0);
  const matchedSelectedRefs = new Set(selectedLegs.flatMap(({ products }) => products.map(({ itemRef }) => String(itemRef))));

  if (
    selectedRefs.size === 0 ||
    matchedSelectedRefs.size !== selectedRefs.size ||
    selectedLegs.length !== 1 ||
    !isSupportedExternalLeg(selectedLegs[0].leg)
  ) {
    return null;
  }

  return selectedLegs[0];
};

const getDepartureMoment = (leg = {}) => {
  const { isoDate, date, departs } = leg.timing || {};
  const departure = isoDate || (date && departs ? `${date}T${departs}` : date);

  return moment(departure);
};

export const isCloneSelectionEligible = ({ booking = {}, selection, now = moment() }) =>
  booking.status === bookingStatus.CONFIRMED &&
  Boolean(selection) &&
  selection.products.length > 0 &&
  selection.products.every(isProductConfirmed) &&
  getDepartureMoment(selection.leg).isValid() &&
  getDepartureMoment(selection.leg).isAfter(now);

const hasOnlyFreeActiveCancellationRules = ({ afterSalesRules = [], isCancelOFEActive } = {}) => {
  const activeCancellationRules = (afterSalesRules || []).filter(
    ({ type, active, override }) => type === 'CANCEL' && Boolean(isCancelOFEActive ? override : active)
  );
  const hasCurrentRuleMarker = activeCancellationRules.some(({ currentActive }) => typeof currentActive === 'boolean');
  const effectiveCancellationRules = hasCurrentRuleMarker
    ? activeCancellationRules.filter(({ currentActive }) => currentActive)
    : activeCancellationRules;

  return effectiveCancellationRules.length > 0 && effectiveCancellationRules.every(({ price }) => Number(price) === 0);
};

export const getFareCondition = (products = []) => {
  if (!products.length || products.some(({ canBeCancelled }) => canBeCancelled !== true)) {
    return FARE_CONDITIONS.NON_CANCELLABLE;
  }

  const isFullyRefundable = products.every((product) => product.canBeRefunded === true && hasOnlyFreeActiveCancellationRules(product));

  return isFullyRefundable ? FARE_CONDITIONS.FULLY_REFUNDABLE : FARE_CONDITIONS.PARTIALLY_OR_NON_REFUNDABLE;
};

export const getCloneWarning = ({ leg, products = [] }) => {
  const fareCondition = getFareCondition(products);
  const isDb = leg?.serviceType?.brandCode === brandCode.DB || leg?.serviceType?.code === serviceTypeCodes.DB;

  if (fareCondition === FARE_CONDITIONS.NON_CANCELLABLE) {
    return isDb ? 1 : 6;
  }

  const isReservedInThirdPartySystem = products.every(({ externalTicketNumber }) => Boolean(externalTicketNumber));

  if (fareCondition === FARE_CONDITIONS.FULLY_REFUNDABLE) {
    return isReservedInThirdPartySystem ? 3 : 5;
  }

  return isReservedInThirdPartySystem ? 2 : 4;
};

const calculatePassengerAge = (birthDate, travelDate) => {
  const birth = moment(birthDate);
  const travel = moment(travelDate);

  if (!birth.isValid() || !travel.isValid()) {
    return null;
  }

  return travel.diff(birth, 'years');
};

const getPassengerAges = (passengers, passengerType, travelDate) =>
  passengers
    .filter(({ type }) => type === passengerType)
    .map(({ birthDate }) => calculatePassengerAge(birthDate, travelDate))
    .filter((age) => age !== null);

const unique = (values) => [...new Set(values.filter(Boolean))];

const getDiscountCards = (passengers) => {
  const cards = passengers
    .flatMap(({ discountCards = [] }) => discountCards)
    .filter(Boolean)
    .filter(({ code }) => Boolean(code))
    .map(({ code, identifier, name, requiresPassengerIdentifier }) => ({
      code,
      ...(identifier !== undefined && identifier !== null && { identifier }),
      ...(name !== undefined && name !== null && { name }),
      ...(typeof requiresPassengerIdentifier === 'boolean' && { requiresPassengerIdentifier })
    }));

  return cards.filter((card, index) => cards.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(card)) === index);
};

export const buildCloneSearchVariables = ({ booking = {}, selection }) => {
  const { leg, products } = selection;
  const selectedPassengerIds = new Set(products.map(({ passengerId }) => String(passengerId)));
  const selectedPassengers = (booking.passengers || []).filter(({ id }) => selectedPassengerIds.has(String(id)));
  const travelDate = leg.timing?.date || moment(leg.timing?.isoDate).format('YYYY-MM-DD');
  const productFamilies = unique(products.map(({ productFamily }) => productFamily?.familyGroup));
  const seniorsAges = getPassengerAges(selectedPassengers, passengerTypes.SENIOR, travelDate);
  const youthAges = getPassengerAges(selectedPassengers, passengerTypes.YOUTH, travelDate);
  const childAges = getPassengerAges(selectedPassengers, passengerTypes.CHILD, travelDate);
  const discountCards = getDiscountCards(selectedPassengers);

  return {
    origin: leg.origin.uic,
    destination: leg.destination.uic,
    outboundDate: travelDate,
    currency: booking.currency,
    trainNumbers: [String(leg.trainNumber)],
    passengers: selectedPassengers.map(({ id, type, disabilityType }) => ({
      id,
      type,
      disabilityType: disabilityType || passengerTypes.NO_HANDICAP
    })),
    productFamilies: productFamilies.length ? productFamilies : [booking.productFamilyGroup].filter(Boolean),
    isYouthAdultConversionActive: false,
    useNewLoyaltySystem: true,
    maxTransfers: 0,
    ...(booking.agent?.contractCode && { contractCode: booking.agent.contractCode }),
    ...(seniorsAges.length && { seniorsAges }),
    ...(youthAges.length && { youthAges }),
    ...(childAges.length && { childAges }),
    ...(discountCards.length && { discountCards })
  };
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim().length > 0;
const sameValue = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);

const legMatches = (candidate, original) =>
  sameValue(candidate.trainNumber, original.trainNumber) &&
  sameValue(candidate.origin?.uic, original.origin?.uic) &&
  sameValue(candidate.destination?.uic, original.destination?.uic);

export const findCloneFare = (data, originalLeg) => {
  const journeys = data?.journeySearch?.outbound?.journeys || [];

  for (const journey of journeys) {
    for (const fare of journey.fares || []) {
      const matchingLeg = (fare.legs || []).find((leg) => legMatches(leg, originalLeg));
      const hasMatchingClass = fare.classOfService?.code === originalLeg.classOfService?.code;

      if (matchingLeg && fare.legs.length === 1 && hasMatchingClass && fare.canBeBooked && fare.bookingItems?.length > 0) {
        return { fare, leg: matchingLeg };
      }
    }
  }

  return null;
};

export const buildCloneSegment = ({ match, direction, passengerIds = [] }) => {
  if (passengerIds.some((passengerId) => !hasValue(passengerId))) {
    return null;
  }

  const selectedPassengerIds = new Set(passengerIds.map(String));
  const items = match.fare.bookingItems
    .filter(
      ({ isAFareAddOn, passengerId, tariffCode }) =>
        isAFareAddOn !== true && hasValue(passengerId) && hasValue(tariffCode) && selectedPassengerIds.has(String(passengerId))
    )
    .map(({ passengerId, tariffCode }) => ({ passengerId, tariffCode }));
  const matchedPassengerIds = new Set(items.map(({ passengerId }) => String(passengerId)));

  if (
    !selectedPassengerIds.size ||
    items.length !== selectedPassengerIds.size ||
    [...selectedPassengerIds].some((passengerId) => !matchedPassengerIds.has(passengerId))
  ) {
    return null;
  }

  return {
    direction: normaliseDirection(direction),
    origin: match.leg.origin.uic,
    destination: match.leg.destination.uic,
    serviceName: match.leg.serviceName,
    serviceIdentifier: match.leg.serviceIdentifier,
    startValidityDate: match.leg.timing.departureDate,
    ...(match.leg.timing.departs && { startValidityTime: match.leg.timing.departs }),
    items
  };
};

export const hasCloneReplacement = ({ booking = {}, segment }) => {
  const expectedItems = segment?.items || [];

  if (
    !segment?.direction ||
    !segment.origin ||
    !segment.destination ||
    !segment.serviceIdentifier ||
    !expectedItems.length ||
    expectedItems.some(({ passengerId, tariffCode }) => !hasValue(passengerId) || !hasValue(tariffCode))
  ) {
    return false;
  }

  const matchingLeg = (booking.pendingCreationLegs || []).find(
    (leg) =>
      normaliseDirection(leg.direction) === normaliseDirection(segment.direction) &&
      sameValue(leg.origin?.uic, segment.origin) &&
      sameValue(leg.destination?.uic, segment.destination) &&
      sameValue(leg.serviceIdentifier, segment.serviceIdentifier)
  );

  if (!matchingLeg) {
    return false;
  }

  const replacementItems = (matchingLeg.products?.length ? matchingLeg.products : matchingLeg.bookingItems || []).filter(
    ({ cancelled, isAFareAddOn, isCancelled }) => !cancelled && !isCancelled && isAFareAddOn !== true
  );

  if (
    replacementItems.some(
      ({ passengerId, tariffCode, tariffDetails }) => !hasValue(passengerId) || !hasValue(tariffCode || tariffDetails?.tariffCode)
    )
  ) {
    return false;
  }

  return (
    replacementItems.length === expectedItems.length &&
    expectedItems.every(({ passengerId, tariffCode }) =>
      replacementItems.some(
        (item) => sameValue(item.passengerId, passengerId) && sameValue(item.tariffCode || item.tariffDetails?.tariffCode, tariffCode)
      )
    )
  );
};
